import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, CheckCircle2, Undo2, Play, RefreshCw, AlertTriangle, Coins, Lock, Eye,
  Flag, Trash2, Star, MessageSquare, Wrench, Waypoints, XCircle, BookOpen, Gauge, Pencil, Sparkles,
} from "lucide-react";
import { BulkReviewTable } from "./BulkReviewTable";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ISSUE_CATS = ["pronunciation", "wrong_emphasis", "pacing", "character_drift", "emotion", "audio_artifact", "bad_transition", "other"];
const STATUS = {
  proposed: "bg-slate-600/25 text-slate-300 ring-slate-500/30",
  ready: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  approved: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  generated: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  stale: "bg-red-500/15 text-red-300 ring-red-500/30",
};

function Chip({ className = "", children, ...rest }) {
  return <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ring-1 ${className}`} {...rest}>{children}</span>;
}

function WholeBookBulkReview({ chapters, passages, projectId, onChanged }) {
  const [open, setOpen] = useState([]);
  const byChap = useMemo(() => {
    const m = {};
    (passages || []).forEach((p) => { (m[p.chapter_id] = m[p.chapter_id] || []).push(p); });
    return m;
  }, [passages]);
  return (
    <Accordion type="multiple" value={open} onValueChange={setOpen} className="mt-6 space-y-2" data-testid="bulk-book-accordion">
      {chapters.map((c) => {
        const ps = byChap[c.id] || [];
        const total = ps.length;
        const toRender = ps.filter((p) => !p.has_active_take).length;
        const toApprove = ps.filter((p) => p.has_active_take && p.status !== "approved").length;
        const approved = ps.filter((p) => p.status === "approved").length;
        return (
          <AccordionItem key={c.id} value={c.id} data-testid={`bulk-book-item-${c.id}`}
            className="overflow-hidden rounded-xl border border-border bg-[#0b0e14]">
            <AccordionTrigger data-testid={`bulk-book-trigger-${c.id}`} className="px-4 py-3 hover:no-underline">
              <div className="flex flex-1 items-center justify-between gap-3 pr-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-serif text-base text-slate-100">{c.label}</span>
                  <span className="whitespace-nowrap font-mono text-[10px] text-slate-500">{c.passage_count} psg</span>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  {total === 0 ? <span className="text-[10px] text-slate-600">no passages</span> : (
                    <>
                      {toRender > 0 && <Chip className="bg-sky-500/15 text-sky-300 ring-sky-500/30">{toRender} to render</Chip>}
                      {toApprove > 0 && <Chip className="bg-amber-500/15 text-amber-300 ring-amber-500/30">{toApprove} to approve</Chip>}
                      {approved > 0 && <Chip className="bg-emerald-500/15 text-emerald-300 ring-emerald-500/30">{approved} approved</Chip>}
                      {toRender === 0 && toApprove === 0 && approved === total && <Chip className="bg-emerald-500/15 text-emerald-300 ring-emerald-500/30">all approved</Chip>}
                    </>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent data-testid={`bulk-book-content-${c.id}`} className="pb-0">
              {open.includes(c.id) && (c.passage_count === 0
                ? <div className="px-4 pb-4 text-sm text-slate-400">This chapter has no passages planned yet.</div>
                : <div className="px-3 pb-3"><BulkReviewTable projectId={projectId} chapterId={c.id} onChanged={onChanged} /></div>)}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function takeSpecs(t) {
  const am = t.audio_meta || null;
  const fmt = t.output_format || "";
  const parts = fmt.split("_");
  const parsedSr = parts[1] ? Number(parts[1]) : null;
  const parsedBr = parts[2] ? Number(parts[2]) : null;
  const isWav = am ? am.container === "wav" : (fmt.startsWith("pcm") || fmt.startsWith("wav"));
  const container = am?.container || (isWav ? "wav" : "mp3");
  const sampleRate = am?.sample_rate || parsedSr;
  const channels = am?.channels ?? null;
  let codec, depth;
  if (isWav) {
    codec = am?.bit_depth ? `pcm_s${am.bit_depth}le` : "pcm";
    depth = am?.bit_depth ? `${am.bit_depth}-bit` : null;
  } else {
    codec = "mp3";
    const br = am?.bitrate_kbps || parsedBr;
    depth = br ? `${br} kbps` : null;
  }
  return { container, codec, sampleRate, channels, depth, verified: !!am };
}

function TakeMeta({ t }) {
  const s = takeSpecs(t);
  const rows = [
    ["Profile", (t.profile || "—")],
    ["Requested", (t.output_format || "—")],
    ["Container", `${s.container?.toUpperCase() || "—"} · ${s.codec}`],
    ["Sample rate", s.sampleRate ? `${s.sampleRate} Hz` : "—"],
    [s.container === "wav" ? "Bit depth" : "Bitrate", s.depth || "—"],
    ["Channels", s.channels != null ? (s.channels === 1 ? "mono" : s.channels === 2 ? "stereo" : `${s.channels}ch`) : "—"],
    ["Duration", t.duration != null ? `${Number(t.duration).toFixed(2)}s` : "—"],
    ["Generated", fmtDate(t.created_at)],
  ];
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px] sm:grid-cols-4" data-testid={`take-format-${t.id}`}>
      {rows.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className="uppercase tracking-wider text-slate-600">{k}</span>
          <span className="text-slate-300">{v}</span>
        </div>
      ))}
      {!s.verified && <div className="col-span-2 text-slate-600 sm:col-span-4">actual container/codec not recorded (legacy take) — values derived from requested format</div>}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono text-lg ${tone === "amber" ? "text-amber-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}

const REPAIR_NUDGES = [
  { key: "stability", label: "Stability", min: 0, max: 1, step: 0.05, hint: "lower = more expressive / variable" },
  { key: "style", label: "Style", min: 0, max: 1, step: 0.05, hint: "higher = stronger delivery / emphasis" },
  { key: "similarity_boost", label: "Similarity", min: 0, max: 1, step: 0.05, hint: "voice adherence" },
  { key: "speed", label: "Speed", min: 0.7, max: 1.2, step: 0.05, hint: "1.0 = natural pace" },
];

function RepairDialog({ issueId, onClose, onGenerated }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const [text, setText] = useState("");
  const [nudge, setNudge] = useState(null);

  // Initial load — pull defaults (exact sentence text + parent settings).
  useEffect(() => {
    let live = true;
    api.post(`/production/issues/${issueId}/repair/preview`, {})
      .then(({ data }) => {
        if (!live) return;
        setP(data);
        setText(data.default_repair_text || data.repair_text || "");
        const b = data.base_settings || {};
        setNudge({
          stability: b.stability ?? 0.5, style: b.style ?? 0,
          similarity_boost: b.similarity_boost ?? 0.75, speed: b.speed ?? 1.0,
        });
        setReady(true);
      })
      .catch((e) => { if (live) setErr(formatApiError(e?.response?.data?.detail)); });
    return () => { live = false; };
  }, [issueId]);

  // Re-estimate credits + audit whenever the user edits text or nudges settings.
  useEffect(() => {
    if (!ready || !nudge) return;
    const t = setTimeout(() => {
      api.post(`/production/issues/${issueId}/repair/preview`, { text_override: text, settings_override: nudge })
        .then(({ data }) => setP((prev) => ({ ...prev, ...data })))
        .catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [text, nudge, ready, issueId]);

  const changed = p && (p.text_overridden || p.settings_overridden);

  const resetDefaults = () => {
    if (!p) return;
    setText(p.default_repair_text || "");
    const b = p.base_settings || {};
    setNudge({ stability: b.stability ?? 0.5, style: b.style ?? 0, similarity_boost: b.similarity_boost ?? 0.75, speed: b.speed ?? 1.0 });
  };

  const generate = async () => {
    if (!text.trim()) { toast.error("Repair text cannot be empty."); return; }
    if (!window.confirm(`This makes a PAID ElevenLabs repair call for ONLY this line (~${p?.estimated_credits} credits). Surrounding sentences are sent as context but are NOT spoken and NOT billed. A new repair clip is saved — the original take is preserved and never replaced automatically. Continue?`)) return;
    setBusy(true);
    try {
      await api.post(`/production/issues/${issueId}/repair/generate`, { confirm: true, text_override: text, settings_override: nudge });
      toast.success("Repair clip generated (original take preserved).");
      if (onGenerated) await onGenerated();
      onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-border bg-[#0d1016] text-slate-200 max-h-[90vh] overflow-y-auto" data-testid="repair-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-slate-100"><Wrench className="h-5 w-5 text-amber-400" /> Repair selection</DialogTitle>
          <DialogDescription className="text-slate-400">Edit the exact text ElevenLabs speaks (respell a word or reshape emphasis) and nudge the voice for this one clip. The original take is always preserved.</DialogDescription>
        </DialogHeader>
        {err && <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="repair-error">{err}</div>}
        {!p && !err && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>}
        {p && (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-[#12161f] p-3 text-xs">
              <span className="text-slate-500">flagged span:</span> <span className="text-amber-300">“{p.span_text}”</span>
              {!p.located && <span className="ml-2 text-red-300">(could not locate — using nearest sentence)</span>}
            </div>

            <div className="rounded-md border border-teal-500/25 bg-teal-500/5 p-3" data-testid="repair-text">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-wider text-teal-400/80">Spoken repair text → ElevenLabs (editable)</div>
                {changed && <button data-testid="repair-reset" onClick={resetDefaults} className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200"><Undo2 className="h-3 w-3" /> Reset to original</button>}
              </div>
              <Textarea
                data-testid="repair-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="resize-y border-border bg-[#0d1016] text-sm text-slate-100"
              />
              <p className="mt-1.5 text-[11px] text-slate-500">Tip: for pronunciation, respell phonetically (e.g. “Prince de Gahl”). For emphasis, rework the wording or use caps, then raise Style / lower Stability below.</p>
            </div>

            <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="repair-nudges">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Per-repair voice nudge {p.settings_overridden && <span className="ml-1 text-amber-300">· modified</span>}</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {REPAIR_NUDGES.map((n) => (
                  <div key={n.key}>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-slate-300">{n.label}</span>
                      <span className="font-mono text-slate-400" data-testid={`repair-nudge-val-${n.key}`}>{Number(nudge?.[n.key] ?? 0).toFixed(2)}</span>
                    </div>
                    <input
                      type="range" min={n.min} max={n.max} step={n.step}
                      value={nudge?.[n.key] ?? 0}
                      onChange={(e) => setNudge((s) => ({ ...s, [n.key]: parseFloat(e.target.value) }))}
                      data-testid={`repair-nudge-${n.key}`}
                      className="mt-1 w-full accent-amber-500"
                    />
                    <div className="text-[10px] text-slate-500">{n.hint}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
              <div className="rounded bg-[#12161f] p-2 text-slate-500" data-testid="repair-prev">previous context (not spoken · not billed): <span className="italic text-slate-400">{p.prev_context || "— none (passage start)"}</span></div>
              <div className="rounded bg-[#12161f] p-2 text-slate-500" data-testid="repair-next">next context (not spoken · not billed): <span className="italic text-slate-400">{p.next_context || "— none (passage end)"}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric label="Repair chars" value={p.characters} />
              <Metric label="Requests" value={p.requests} />
              <Metric label="Est. credits" value={p.estimated_credits} tone="amber" />
              <Metric label="Output" value={p.output_format} />
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-1 text-[11px]" data-testid="repair-profile-badge">
              <span className="font-mono uppercase tracking-wider text-amber-300">Production profile</span>
              <span className="text-slate-300">{p.request_preview?.output_format_label || p.output_format}</span>
            </div>
            <div className="text-xs text-slate-400"><Coins className="mr-1 inline h-3 w-3 text-amber-400" />{p.billing_rate_label} · context is not billed</div>

            {p.base_take_stale && (
              <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="repair-stale">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> The parent take is STALE — the manuscript&apos;s transformed text has changed since it was generated. This repair targets the parent take&apos;s original text; consider regenerating the whole passage first.
              </div>
            )}
            {!p.mapping_available && (
              <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="repair-no-map">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {p.mapping_note}
              </div>
            )}

            {p.applied_rules?.length > 0 && (
              <div className="rounded-md border border-border bg-[#12161f] p-3 text-xs" data-testid="repair-rules">
                <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Same pronunciation as parent take ({p.applied_rules.length})</div>
                {p.applied_rules.map((r, i) => (
                  <div key={i} className="text-slate-300">{r.type === "generation_substitution"
                    ? <>“{r.written}” → <span className="font-mono text-teal-300">“{r.generation}”</span></>
                    : <>“{r.written}” → <span className="font-mono text-amber-300">{r.phoneme ? `/${r.phoneme}/` : r.alias}</span></>}</div>
                ))}
              </div>
            )}

            <details className="rounded-md border border-slate-600/40 bg-[#0d1016] p-3">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-slate-400" data-testid="repair-request-summary">Exact ElevenLabs repair request (audit)</summary>
              <div className="mt-2 space-y-0.5 text-[11px] text-slate-300">
                <div><span className="text-slate-500">endpoint</span> <span className="font-mono break-all">{p.request_preview.endpoint}</span></div>
                <div><span className="text-slate-500">model</span> <span className="font-mono">{p.request_preview.model_id}</span></div>
                <div><span className="text-slate-500">text</span> <span className="font-mono break-all">{p.request_preview.text}</span></div>
                <div><span className="text-slate-500">voice_settings</span> <span className="font-mono">{JSON.stringify(p.request_preview.voice_settings)}</span></div>
                <div><span className="text-slate-500">previous_text</span> <span className="font-mono">{p.request_preview.previous_text || "none"}</span></div>
                <div><span className="text-slate-500">next_text</span> <span className="font-mono">{p.request_preview.next_text || "none"}</span></div>
                <div className="text-emerald-300">{p.request_preview.spoken_characters_note}</div>
              </div>
            </details>

            <Button data-testid="repair-generate" onClick={generate} disabled={busy || !text.trim()} className="w-full gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} Confirm & generate repair (paid · original take kept)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PassageDetail({ pid, onClose, onChange }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [span, setSpan] = useState("");
  const [cat, setCat] = useState("pronunciation");
  const [note, setNote] = useState("");
  const [direction, setDirection] = useState("");
  const [repairIssueId, setRepairIssueId] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [ovr, setOvr] = useState(null);
  const [showOvr, setShowOvr] = useState(false);
  const [emph, setEmph] = useState(null);
  const [emphBusy, setEmphBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/production/passages/${pid}`); setD(data); setSpeed((s) => s == null ? data.effective_speed : s); setOvr((o) => o == null ? (data.narration_override ?? "") : o); setEmph((e) => e == null ? (data.emphasis?.marks ?? []).map((m) => ({ ...m })) : e); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); if (onChange) await onChange(); };

  const setMark = (occ, patch) => setEmph((ms) => ms.map((m) => (m.occurrence === occ ? { ...m, ...patch } : m)));

  const proposeEmphasis = async () => {
    setEmphBusy(true);
    try {
      const { data } = await api.post(`/production/passages/${pid}/emphasis/propose`, {});
      setEmph((data.marks ?? []).map((m) => ({ ...m })));
      toast.success(`Suggested emphasis for ${data.proposed} mark(s).`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setEmphBusy(false); }
  };

  const saveEmphasis = async () => {
    setEmphBusy(true);
    try {
      const payload = { marks: (emph ?? []).map((m) => ({
        occurrence: m.occurrence, inner: m.inner, order: m.order ?? null,
        direction: m.direction || "", emphasized: m.emphasized || "", confirmed: m.status === "confirmed",
      })) };
      const { data } = await api.put(`/production/passages/${pid}/emphasis`, payload);
      setEmph((data.emphasis?.marks ?? []).map((m) => ({ ...m })));
      toast.success("Emphasis saved — applies on your next (re)generate.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setEmphBusy(false); }
  };

  const saveOverride = async (clear = false) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/production/passages/${pid}/narration`, { text: clear ? null : ovr });
      setOvr(data.narration_override ?? "");
      toast.success(clear ? "Narration override cleared — back to manuscript text." : "Narration text saved — applies on your next (re)generate.");
      if (clear) setShowOvr(false);
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSpeed = async (val, clear = false) => {
    try {
      const { data } = await api.put(`/production/passages/${pid}/speed`, { speed: clear ? null : val });
      setSpeed(data.effective_speed);
      toast.success(clear ? `Reset to project default (${data.project_speed.toFixed(2)}×)` : `Passage speed set to ${val.toFixed(2)}× — applies on your next (re)generate`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const act = async (verb, paid = false) => {
    if (paid && !window.confirm(`This makes a PAID ElevenLabs call for this single passage (~${d?.estimated_credits} credits) and creates a NEW take (prior takes are kept). Continue?`)) return;
    setBusy(true);
    try {
      const bodyPayload = verb === "regenerate" ? { confirm: true, direction } : (paid ? { confirm: true } : {});
      await api.post(`/production/passages/${pid}/${verb}`, bodyPayload);
      toast.success({ approve: "Passage approved.", return: "Returned.", generate: "Take generated.", regenerate: "New take generated (prior takes kept)." }[verb]);
      setDirection("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const takeAction = async (tid, verb) => {
    setBusy(true);
    try {
      const url = verb === "activate" ? `/production/passages/${pid}/takes/${tid}/activate` : `/production/takes/${tid}/${verb}`;
      await api.post(url, {});
      toast.success({ activate: "Set as active take.", approve: "Take approved & set active.", reject: "Take rejected." }[verb]);
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const captureSelection = () => {
    const s = window.getSelection ? window.getSelection().toString().trim() : "";
    if (s) setSpan(s.slice(0, 200));
  };

  const addIssue = async () => {
    try {
      await api.post(`/production/passages/${pid}/issues`, { category: cat, span_text: span, note, take_id: d.active_take_id });
      toast.success("Issue logged.");
      setSpan(""); setNote("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const issueAction = async (iid, verb) => {
    try {
      await api.post(`/production/issues/${iid}/${verb}`, verb === "resolve" ? { resolution: "Resolved" } : {});
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const deleteIssue = async (iid) => { try { await api.delete(`/production/issues/${iid}`); await refresh(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };

  const repairAction = async (rid, verb) => {
    setBusy(true);
    try {
      await api.post(`/production/repairs/${rid}/${verb}`, {});
      toast.success(verb === "approve" ? "Repair approved — linked to the issue and queued for Phase 7 placement."
        : verb === "place" ? "Repair attached to the current active take — it will stitch into the export."
        : "Repair rejected.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!d) return (
    <Dialog open onOpenChange={onClose}><DialogContent className="border-border bg-[#0d1016]"><DialogHeader><DialogTitle className="sr-only">Loading passage</DialogTitle></DialogHeader><div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></DialogContent></Dialog>
  );
  const rp = d.request_preview;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl border-border bg-[#0d1016] text-slate-200 max-h-[90vh] overflow-y-auto" data-testid="passage-detail">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-slate-100">Passage · ¶{d.start_body}–{d.end_body} {d.pilot_set && <span className="text-amber-300">· {d.pilot_set}</span>}</DialogTitle>
          <DialogDescription className="text-slate-400">Review the original vs transformed text and the exact request before any paid generation.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Chip className={STATUS[d.status]}>{d.status}</Chip>
            <span className="flex items-center gap-1 rounded-full bg-slate-800/70 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-300"><Lock className="h-3 w-3" /> {d.narrator?.name}</span>
          </div>

          {(d.prev_context || d.next_context) && (
            <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
              <div className="rounded bg-[#12161f] p-2 text-slate-500">prev context (not spoken): <span className="italic">{d.prev_context || "—"}</span></div>
              <div className="rounded bg-[#12161f] p-2 text-slate-500">next context (not spoken): <span className="italic">{d.next_context || "—"}</span></div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="rounded-md border border-border bg-[#12161f] p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Original manuscript text (unchanged)</div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-400" data-testid="passage-original">{d.original_text}</p>
            </div>
            <div className="rounded-md border border-teal-500/25 bg-teal-500/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-teal-400/80">Transformed generation text → ElevenLabs <span className="text-slate-500">(select any span to log an issue)</span></div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-100 selection:bg-amber-400/40" data-testid="passage-transformed" onMouseUp={captureSelection}>{d.transformed_text}</p>
            </div>
          </div>

          {d.take_stale && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="passage-stale">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> The active take is STALE — the transformed text changed since it was generated. Regenerate to refresh (prior takes are kept).
            </div>
          )}

          {/* Narration text override */}
          <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="passage-narration-override">
            <button data-testid="narration-override-toggle" onClick={() => setShowOvr((s) => !s)}
              className="flex w-full items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500 hover:text-amber-300">
              <span className="flex items-center gap-1.5"><Pencil className="h-3 w-3" /> Narration text override {d.has_narration_override && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] text-amber-300 ring-1 ring-amber-500/30">active</span>}</span>
              <span>{showOvr ? "hide" : "edit"}</span>
            </button>
            {showOvr && (
              <div className="mt-2 space-y-2">
                <p className="text-[11px] text-slate-500">Override the exact words spoken for this passage without changing the manuscript. Leave empty to use the manuscript text. Applies on your next (re)generate.</p>
                <Textarea data-testid="narration-override-input" value={ovr ?? ""} onChange={(e) => setOvr(e.target.value)}
                  rows={4} placeholder={d.transformed_text} className="resize-y bg-[#0d1016] font-serif text-sm text-slate-100" />
                <div className="flex items-center gap-2">
                  <Button size="sm" data-testid="narration-override-save" onClick={() => saveOverride(false)} disabled={busy}
                    className="h-7 gap-1 bg-amber-500 text-slate-950 text-xs hover:bg-amber-400"><CheckCircle2 className="h-3 w-3" /> Save override</Button>
                  {d.has_narration_override && (
                    <Button size="sm" variant="outline" data-testid="narration-override-clear" onClick={() => saveOverride(true)} disabled={busy}
                      className="h-7 gap-1 border-border text-slate-300 text-xs"><XCircle className="h-3 w-3" /> Clear</Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {d.emphasis?.has_marks && (
            <div className="rounded-md border border-fuchsia-500/25 bg-fuchsia-500/[0.05] p-3" data-testid="passage-emphasis">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-fuchsia-300/90">
                  <Sparkles className="h-3.5 w-3.5" /> Narration emphasis · {d.emphasis.marks.length} *marked* {d.emphasis.marks.length === 1 ? "word" : "words"}
                  {d.emphasis.confirmed_count > 0 && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] text-emerald-300 ring-1 ring-emerald-500/30">{d.emphasis.confirmed_count} confirmed</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" data-testid="emphasis-suggest" onClick={proposeEmphasis} disabled={emphBusy}
                    className="h-7 gap-1 bg-fuchsia-500/90 text-slate-950 text-xs hover:bg-fuchsia-400">
                    {emphBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Suggest with AI
                  </Button>
                  <Button size="sm" variant="outline" data-testid="emphasis-save" onClick={saveEmphasis} disabled={emphBusy}
                    className="h-7 gap-1 border-fuchsia-500/30 text-fuchsia-200 text-xs hover:bg-fuchsia-500/10"><CheckCircle2 className="h-3 w-3" /> Save{(emph ?? []).filter((m) => m.status === "confirmed").length ? ` (${(emph ?? []).filter((m) => m.status === "confirmed").length} to apply)` : ""}</Button>
                </div>
              </div>
              <p className="mb-2 text-[11px] text-slate-500">You wrapped these words in *asterisks* to request emphasis (the narrator never voices the asterisks). Tap <span className="text-fuchsia-300">Suggest with AI</span> for wording, flip <span className="text-emerald-300">Apply to narration</span> on the ones you want, then <span className="text-fuchsia-300">Save</span> — and <span className="text-amber-300">regenerate the take</span> to actually hear it. Saving does not spend ElevenLabs credits.</p>
              <div className="space-y-2">
                {(emph ?? []).map((m) => (
                  <div key={m.occurrence} data-testid={`emphasis-mark-${m.occurrence}`} className="rounded-md border border-border bg-[#12161f] p-2.5">
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="rounded bg-fuchsia-500/15 px-1.5 py-0.5 font-mono text-[11px] text-fuchsia-200 ring-1 ring-fuchsia-500/30">{m.inner}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ring-1 ${m.status === "confirmed" ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" : m.status === "proposed" ? "bg-amber-500/15 text-amber-300 ring-amber-500/30" : "bg-slate-600/25 text-slate-300 ring-slate-500/30"}`} data-testid={`emphasis-status-${m.occurrence}`}>{m.status}</span>
                      <span className="truncate text-[11px] italic text-slate-500">&quot;{m.sentence}&quot;</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <div className="mb-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">Emphasized wording → spoken</div>
                        <Input data-testid={`emphasis-text-${m.occurrence}`} value={m.emphasized || ""} placeholder={m.inner}
                          onChange={(e) => setMark(m.occurrence, { emphasized: e.target.value })}
                          className="h-8 bg-[#0d1016] font-serif text-sm text-slate-100" />
                      </div>
                      <div>
                        <div className="mb-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">Narration direction</div>
                        <Input data-testid={`emphasis-direction-${m.occurrence}`} value={m.direction || ""} placeholder="e.g. stress firmly, slight pause before"
                          onChange={(e) => setMark(m.occurrence, { direction: e.target.value })}
                          className="h-8 bg-[#0d1016] text-sm text-slate-300" />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/50 pt-2">
                      <span className="text-[11px] text-slate-500">{m.status === "confirmed" ? "Will be applied on the next regenerate" : "Suggested only — not applied yet"}</span>
                      <label className="flex items-center gap-2 text-[11px] text-slate-300">
                        <span className={m.status === "confirmed" ? "text-emerald-300" : "text-slate-400"}>Apply to narration</span>
                        <Switch data-testid={`emphasis-confirm-${m.occurrence}`}
                          checked={m.status === "confirmed"}
                          disabled={!(m.emphasized || "").trim()}
                          onCheckedChange={(v) => setMark(m.occurrence, { status: v ? "confirmed" : "proposed" })}
                          className="data-[state=checked]:bg-emerald-500" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="Characters" value={d.characters} />
            <Metric label="Requests" value={d.requests} />
            <Metric label="Est. credits" value={d.estimated_credits} tone="amber" />
            <Metric label="Output" value={d.output_format} />
          </div>
          <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-1 text-[11px]" data-testid="passage-profile-badge">
            <span className="font-mono uppercase tracking-wider text-amber-300">Production profile</span>
            <span className="text-slate-300">{d.output_format_label || d.output_format}</span>
          </div>
          <div className="text-xs text-slate-400"><Coins className="mr-1 inline h-3 w-3 text-amber-400" />{d.billing_rate_label} · context is not billed</div>

          <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="passage-speed">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Gauge className="h-3 w-3" /> Narration speed · applies on next (re)generate</div>
              <span className="font-mono text-sm text-slate-100" data-testid="passage-speed-val">{(speed ?? 1).toFixed(2)}×</span>
            </div>
            <input type="range" min={d.speed_bounds?.[0] ?? 0.7} max={d.speed_bounds?.[1] ?? 1.2} step={0.05}
              value={speed ?? 1} onChange={(e) => setSpeed(parseFloat(e.target.value))}
              onMouseUp={(e) => saveSpeed(parseFloat(e.target.value))} onTouchEnd={(e) => saveSpeed(parseFloat(e.target.value))}
              data-testid="passage-speed-slider" className="w-full accent-amber-500" />
            <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
              <span>0.7× slower · 1.0× natural · 1.2× faster</span>
              <span>project default {(d.project_speed ?? 1).toFixed(2)}×{d.speed_override != null && <button data-testid="passage-speed-reset" onClick={() => saveSpeed(null, true)} className="ml-1.5 text-slate-400 underline hover:text-amber-300">use default</button>}</span>
            </div>
          </div>

          {d.applied_rules?.length > 0 && (
            <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="passage-applied-rules">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Applied pronunciation ({d.applied_rules.length})</div>
              {d.applied_rules.map((r, i) => (
                <div key={i} className="text-xs text-slate-300">
                  {r.type === "generation_substitution"
                    ? <>“{r.written}” → <span className="font-mono text-teal-300">“{r.generation}”</span> <span className="text-slate-500">(homograph · {r.sense})</span></>
                    : <>“{r.written}” → <span className="font-mono text-amber-300">{r.phoneme ? `/${r.phoneme}/` : r.alias}</span> <span className="text-slate-500">(dictionary rule)</span></>}
                </div>
              ))}
            </div>
          )}

          <details className="rounded-md border border-slate-600/40 bg-[#0d1016] p-3">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-slate-400" data-testid="passage-request-summary">Exact ElevenLabs request (audit)</summary>
            <div className="mt-2 space-y-0.5 text-[11px] text-slate-300">
              <div><span className="text-slate-500">endpoint</span> <span className="font-mono">{rp.endpoint}</span></div>
              <div><span className="text-slate-500">voice</span> <span className="font-mono">{rp.voice_id} ({rp.voice_name})</span></div>
              <div><span className="text-slate-500">model</span> <span className="font-mono">{rp.model_id}</span></div>
              <div><span className="text-slate-500">voice_settings</span> <span className="font-mono">{JSON.stringify(rp.voice_settings)}</span></div>
              <div><span className="text-slate-500">dictionary_locators</span> <span className="font-mono">{rp.pronunciation_dictionary_locators ? JSON.stringify(rp.pronunciation_dictionary_locators) : "none"}</span></div>
              <div className="text-emerald-300">{rp.spoken_characters_note}</div>
            </div>
          </details>

          {/* Issue marker */}
          <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="issue-marker">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Mark an issue</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <Input data-testid="issue-span" value={span} onChange={(e) => setSpan(e.target.value)} placeholder="Selected span (highlight text above, or type it)" className="bg-[#0d1016]" />
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="bg-[#0d1016] sm:w-48" data-testid="issue-category"><SelectValue /></SelectTrigger>
                <SelectContent>{ISSUE_CATS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea data-testid="issue-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Direction / note (e.g. slow the delivery, soften emphasis)" className="mt-2 bg-[#0d1016]" rows={2} />
            <Button data-testid="issue-add" size="sm" onClick={addIssue} disabled={!span && !note} className="mt-2 gap-2 bg-slate-200 text-slate-900 hover:bg-white"><Flag className="h-3.5 w-3.5" /> Log issue</Button>
          </div>

          {/* Issues list */}
          {d.issues?.length > 0 && (
            <div className="rounded-md border border-border bg-[#12161f] p-3" data-testid="issues-list">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Issues ({d.open_issue_count} open)</div>
              <div className="space-y-1.5">
                {d.issues.map((i) => (
                  <div key={i.id} className="flex items-start justify-between gap-2 rounded bg-[#0d1016] p-2 text-xs" data-testid={`issue-${i.id}`}>
                    <div className="min-w-0">
                      <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] uppercase ${i.state === "open" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{i.state}</span>
                      <span className="text-slate-400">{i.category.replace(/_/g, " ")}</span>
                      {i.span_text && <span className="text-slate-300"> · “{i.span_text}”</span>}
                      {i.note && <div className="mt-0.5 text-slate-500"><MessageSquare className="mr-1 inline h-3 w-3" />{i.note}</div>}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {i.span_text && <button data-testid={`issue-repair-${i.id}`} title="Repair this selection" onClick={() => setRepairIssueId(i.id)} className="text-amber-400 hover:text-amber-300"><Wrench className="h-4 w-4" /></button>}
                      {i.state === "open"
                        ? <button data-testid={`issue-resolve-${i.id}`} onClick={() => issueAction(i.id, "resolve")} className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 className="h-4 w-4" /></button>
                        : <button onClick={() => issueAction(i.id, "reopen")} className="text-amber-400 hover:text-amber-300"><Undo2 className="h-4 w-4" /></button>}
                      <button onClick={() => deleteIssue(i.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Repair clips — targeted line repairs (original take always preserved) */}
          {d.repairs?.length > 0 && (
            <div className="space-y-2" data-testid="repairs-list">
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Wrench className="h-3 w-3 text-amber-400" /> Repair clips ({d.repairs.length}) · original take is never replaced automatically</div>
              {d.repairs.map((r) => {
                const baseTake = d.takes?.find((t) => t.id === r.base_take_id);
                return (
                  <div key={r.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`repair-${r.id}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-300">
                        repair · <span className={r.approval === "approved" ? "text-emerald-300" : r.approval === "rejected" ? "text-red-300" : "text-slate-400"}>{r.approval}</span>
                        {r.stitch_ready
                          ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-300"><Waypoints className="h-3 w-3" /> stitch-ready</span>
                          : <span className="ml-2 inline-flex items-center gap-1 text-amber-300"><AlertTriangle className="h-3 w-3" /> not stitch-ready</span>}
                      </span>
                      <span className="font-mono text-slate-500">{r.char_count} chars · {r.usage_available ? `${r.actual_credits} cr` : `~${r.estimated_credits} cr est · usage n/a`}</span>
                    </div>
                    {!r.stitch_ready && r.stitch_reason && <div className="mb-1 text-[11px] text-amber-300/80">{r.stitch_reason}</div>}
                    {(r.text_overridden || r.settings_overridden) && (
                      <div className="mb-1 flex flex-wrap gap-1.5" data-testid={`repair-edited-${r.id}`}>
                        {r.text_overridden && <span className="rounded bg-teal-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-teal-300 ring-1 ring-teal-500/30">edited text</span>}
                        {r.settings_overridden && <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/30">voice nudged</span>}
                      </div>
                    )}
                    <p className="mb-2 whitespace-pre-wrap text-xs text-slate-200">{r.repair_text}</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div className="rounded bg-[#0d1016] p-2">
                        <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-slate-500">Repaired line</div>
                        <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${r.audio_url}`} data-testid={`repair-audio-${r.id}`} />
                      </div>
                      <div className="rounded bg-[#0d1016] p-2">
                        <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-slate-500">Original take (full passage) — compare</div>
                        {baseTake
                          ? <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${baseTake.audio_url}`} data-testid={`repair-base-audio-${r.id}`} />
                          : <div className="text-[11px] text-slate-500">base take unavailable</div>}
                      </div>
                    </div>
                    {r.placement && (
                      <div className="mb-2 text-[11px]" data-testid={`repair-placement-${r.id}`}>
                        {r.placement.placeable
                          ? <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" /> {r.placement.state === "rebased" ? "Auto-placed on current active take (will stitch into export)" : "Placed on active take — will stitch into export"}</span>
                          : <span className="inline-flex items-start gap-1 text-amber-300/90"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {r.placement.reason}</span>}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {r.approval !== "approved" && <Button size="sm" variant="outline" data-testid={`repair-approve-${r.id}`} onClick={() => repairAction(r.id, "approve")} disabled={busy} className="h-7 gap-1 border-emerald-500/40 text-emerald-200 text-xs"><CheckCircle2 className="h-3 w-3" /> Approve repair</Button>}
                      {r.placement?.can_place && <Button size="sm" variant="outline" data-testid={`repair-place-${r.id}`} onClick={() => repairAction(r.id, "place")} disabled={busy} className="h-7 gap-1 border-sky-500/40 text-sky-200 text-xs"><Waypoints className="h-3 w-3" /> Place on active take</Button>}
                      {r.approval !== "rejected" && <Button size="sm" variant="outline" data-testid={`repair-reject-${r.id}`} onClick={() => repairAction(r.id, "reject")} disabled={busy} className="h-7 gap-1 border-red-500/40 text-red-200 text-xs"><XCircle className="h-3 w-3" /> Reject</Button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Takes — comparison + lifecycle (prior takes never deleted) */}
          {d.takes?.length > 0 && (
            <div className="space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Takes · compare & choose ({d.takes.length})</div>
              {d.takes.map((t) => (
                <div key={t.id} className={`rounded-lg border p-3 ${t.is_active ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-[#12161f]"}`} data-testid={`take-${t.id}`}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-slate-300">
                      {t.is_active && <Star className="inline h-3 w-3 text-emerald-400" />}
                      <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ring-1 ${t.profile === "audition" ? "bg-amber-500/10 text-amber-300 ring-amber-500/30" : "bg-sky-500/10 text-sky-300 ring-sky-500/30"}`} data-testid={`take-profile-${t.id}`}>{t.profile === "audition" ? "Audition/Test" : "Production"}</span>
                      {t.is_active && <Chip className="bg-emerald-500/15 text-emerald-300 ring-emerald-500/30">Active</Chip>}
                      <span className={t.approval === "approved" ? "text-emerald-300" : t.approval === "rejected" ? "text-red-300" : "text-slate-400"}>{t.kind} · {t.approval}</span>
                      {t.stitch_ready
                        ? <span className="inline-flex items-center gap-1 text-emerald-300" title="Word/character timestamps captured"><Waypoints className="h-3 w-3" /> stitch-ready</span>
                        : <span className="inline-flex items-center gap-1 text-slate-500" title={t.stitch_reason}><AlertTriangle className="h-3 w-3" /> alignment unavailable</span>}
                      {t.direction && <span className="text-slate-500"> · dir: {t.direction}</span>}
                    </span>
                    <span className="whitespace-nowrap font-mono text-slate-500">{t.char_count} chars · {t.usage_available ? `${t.actual_credits} cr` : `~${t.estimated_credits} cr est · usage n/a`}</span>
                  </div>
                  <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${t.audio_url}`} data-testid={`take-audio-${t.id}`} />
                  <TakeMeta t={t} />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {!t.is_active && <Button size="sm" variant="outline" data-testid={`take-activate-${t.id}`} onClick={() => takeAction(t.id, "activate")} disabled={busy} className="h-7 gap-1 text-xs">Set active</Button>}
                    {t.approval !== "approved" && <Button size="sm" variant="outline" data-testid={`take-approve-${t.id}`} onClick={() => takeAction(t.id, "approve")} disabled={busy} className="h-7 gap-1 border-emerald-500/40 text-emerald-200 text-xs"><CheckCircle2 className="h-3 w-3" /> Approve</Button>}
                    {t.approval !== "rejected" && <Button size="sm" variant="outline" data-testid={`take-reject-${t.id}`} onClick={() => takeAction(t.id, "reject")} disabled={busy} className="h-7 gap-1 border-red-500/40 text-red-200 text-xs">Reject</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-2">
              {d.status !== "approved" ? (
                <Button data-testid="passage-approve" onClick={() => act("approve")} disabled={busy} className="gap-2 bg-sky-500 text-slate-950 hover:bg-sky-400"><CheckCircle2 className="h-4 w-4" /> Approve passage</Button>
              ) : (
                <Button data-testid="passage-return" variant="outline" onClick={() => act("return")} disabled={busy} className="gap-2"><Undo2 className="h-4 w-4" /> Return</Button>
              )}
              {d.takes?.length === 0 && (
                <Button data-testid="passage-generate" onClick={() => act("generate", true)} disabled={busy || d.status !== "approved"} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Generate (paid)
                </Button>
              )}
              {d.status !== "approved" && d.takes?.length === 0 && <span className="text-[11px] text-slate-500"><AlertTriangle className="mr-1 inline h-3 w-3" />Approve the passage to enable generation.</span>}
            </div>
            {d.takes?.length > 0 && (
              <div className="rounded-md border border-amber-500/20 bg-amber-500/5 p-2">
                <Textarea data-testid="regen-direction" value={direction} onChange={(e) => setDirection(e.target.value)} placeholder="Optional direction note for this regeneration (recorded on the new take)" className="bg-[#0d1016]" rows={2} />
                <Button data-testid="passage-regenerate" onClick={() => act("regenerate", true)} disabled={busy} className="mt-2 gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate as new take (paid · prior takes kept)
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      {repairIssueId && <RepairDialog issueId={repairIssueId} onClose={() => setRepairIssueId(null)} onGenerated={refresh} />}
    </Dialog>
  );
}

function PassageCard({ p, onOpen }) {
  return (
    <button data-testid={`passage-card-${p.id}`} onClick={() => onOpen(p.id)}
      className="flex w-full items-center justify-between rounded-lg border border-border bg-[#12161f] p-3 text-left transition-all hover:border-amber-500/40">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-slate-500">#{p.index + 1}</span>
          <span className="text-sm text-slate-200">¶{p.start_body}–{p.end_body}</span>
          <Chip className={STATUS[p.status]}>{p.status}</Chip>
          {p.has_active_take && <Chip className="bg-emerald-500/15 text-emerald-300 ring-emerald-500/30">take</Chip>}
          {p.stale && <Chip className="bg-red-500/15 text-red-300 ring-red-500/30">stale</Chip>}
          {p.has_narration_override && <Chip className="bg-amber-500/15 text-amber-300 ring-amber-500/30">override</Chip>}
          {p.emphasis_marks > 0 && (
            <Chip data-testid={`emphasis-chip-${p.id}`}
              title={`${p.emphasis_confirmed} of ${p.emphasis_marks} emphasis mark${p.emphasis_marks === 1 ? "" : "s"} confirmed`}
              className={p.emphasis_confirmed >= p.emphasis_marks
                ? "bg-fuchsia-500/20 text-fuchsia-200 ring-fuchsia-500/40"
                : "bg-fuchsia-500/10 text-fuchsia-300/80 ring-fuchsia-500/25"}>
              <span className="inline-flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" />{p.emphasis_confirmed}/{p.emphasis_marks}</span>
            </Chip>
          )}
          {p.pilot_set && <Chip className="bg-amber-500/15 text-amber-300 ring-amber-500/30">pilot</Chip>}
        </div>
        <div className="mt-0.5 font-mono text-[10px] text-slate-500">{p.char_count} chars · {p.para_count} paras · ~{p.char_count} cr</div>
      </div>
      <Eye className="h-4 w-4 shrink-0 text-slate-600" />
    </button>
  );
}

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pilot", label: "Ready pilots" },
  { key: "proposed", label: "Proposed" },
  { key: "approved", label: "Approved" },
  { key: "generated", label: "Has take" },
];

function passageMatches(p, filter) {
  if (filter === "all") return true;
  if (filter === "pilot") return !!p.pilot_set;
  if (filter === "generated") return !!p.has_active_take;
  return p.status === filter;
}

export function ProductionDesk() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("cards");
  const [projSpeed, setProjSpeed] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/production/passages`);
    setData(data);
    return data;
  }, [projectId]);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => { setProject(r.data); setProjSpeed((s) => s == null ? (r.data.narration_speed ?? 1.0) : s); }).catch(() => {});
    api.post(`/projects/${projectId}/production/plan`).finally(load);
  }, [projectId, load]);

  const saveProjSpeed = async (val) => {
    try {
      const { data } = await api.put(`/projects/${projectId}/narration-speed`, { speed: val });
      setProjSpeed(data.narration_speed);
      toast.success(`Default narration speed set to ${data.narration_speed.toFixed(2)}× — applies to all new generations`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Default to the first chapter that has passages.
  useEffect(() => {
    if (data && activeChapter === null && data.chapters?.length) {
      const first = data.chapters.find((c) => c.passage_count > 0) || data.chapters[0];
      setActiveChapter(first.id);
    }
  }, [data, activeChapter]);

  const chapterPassages = useMemo(
    () => (data && activeChapter ? data.passages.filter((p) => p.chapter_id === activeChapter) : []),
    [data, activeChapter]);
  const visible = useMemo(
    () => chapterPassages.filter((p) => passageMatches(p, statusFilter)),
    [chapterPassages, statusFilter]);

  if (!data) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  const chapters = data.chapters || [];
  const activeCh = chapters.find((c) => c.id === activeChapter);
  const chCounts = {
    total: chapterPassages.length,
    approved: chapterPassages.filter((p) => p.status === "approved").length,
    generated: chapterPassages.filter((p) => p.has_active_take).length,
    proposed: chapterPassages.filter((p) => p.status === "proposed").length,
    pilot: chapterPassages.filter((p) => p.pilot_set).length,
  };

  return (
    <div>
      <button onClick={() => navigate(`/studio/projects/${projectId}`)} className="mb-4 flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-300" data-testid="production-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to production
      </button>
      <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 5 · Production</div>
      <h1 className="font-serif text-4xl tracking-tight text-slate-100">Production Desk</h1>
      <p className="mt-1 text-sm text-slate-400">{project?.title} · Scott narrates every passage with his locked settings. Pick a chapter to focus on its takes.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Metric label="Passages" value={data.counts.total} />
        <Metric label="Ready pilots" value={data.counts.ready} tone="amber" />
        <Metric label="Proposed" value={data.counts.proposed} />
        <Metric label="Approved" value={data.counts.approved} />
        <Metric label="Generated" value={data.counts.generated} />
      </div>

      {/* Project default narration speed */}
      <div className="mt-4 rounded-lg border border-border bg-[#12161f] p-4" data-testid="project-speed">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Gauge className="h-3 w-3" /> Default narration speed · applies to all new generations</div>
          <span className="font-mono text-sm text-slate-100" data-testid="project-speed-val">{(projSpeed ?? 1).toFixed(2)}×</span>
        </div>
        <input type="range" min={data.speed_bounds?.[0] ?? 0.7} max={data.speed_bounds?.[1] ?? 1.2} step={0.05}
          value={projSpeed ?? 1} onChange={(e) => setProjSpeed(parseFloat(e.target.value))}
          onMouseUp={(e) => saveProjSpeed(parseFloat(e.target.value))} onTouchEnd={(e) => saveProjSpeed(parseFloat(e.target.value))}
          data-testid="project-speed-slider" className="mt-2 w-full accent-amber-500" />
        <div className="mt-1 text-[11px] text-slate-500">0.7× slower · 1.0× natural · 1.2× faster. Each passage can override this in its detail panel. No audio changes until you (re)generate.</div>
      </div>

      {/* Chapter selector */}
      <div className="mt-8 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><BookOpen className="h-3 w-3" /> Chapter</div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="chapter-tabs">
        {chapters.map((c) => (
          <button key={c.id} data-testid={`chapter-tab-${c.id}`} onClick={() => { setActiveChapter(c.id); setStatusFilter("all"); }}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${activeChapter === c.id
              ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
              : "border-border bg-[#12161f] text-slate-300 hover:border-amber-500/30"}`}>
            {c.label} <span className="ml-1 font-mono text-[10px] text-slate-500">{c.passage_count}</span>
          </button>
        ))}
      </div>

      {activeCh && (
        <div className="mt-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-serif text-2xl text-slate-100">{viewMode === "bulk" ? "Bulk review · whole book" : activeCh.label}</h2>
            <div className="flex items-center gap-3">
              <div className="flex rounded-lg border border-border bg-[#0d1016] p-0.5" data-testid="view-mode-toggle">
                <button data-testid="view-cards" onClick={() => setViewMode("cards")}
                  className={`rounded-md px-3 py-1 text-xs transition-all ${viewMode === "cards" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:text-slate-200"}`}>Cards</button>
                <button data-testid="view-bulk" onClick={() => setViewMode("bulk")}
                  className={`rounded-md px-3 py-1 text-xs transition-all ${viewMode === "bulk" ? "bg-amber-500/15 text-amber-200" : "text-slate-400 hover:text-slate-200"}`}>Bulk Review</button>
              </div>
              <div className="font-mono text-[11px] text-slate-500">
                {viewMode === "bulk"
                  ? `${data.counts.total} passages · ${data.counts.approved} approved · tap a chapter to review`
                  : `${chCounts.total} passages · ${chCounts.approved} approved · ${chCounts.generated} with take · ${chCounts.proposed} proposed`}
              </div>
            </div>
          </div>

          {viewMode === "bulk" ? (
            <WholeBookBulkReview chapters={chapters} passages={data.passages} projectId={projectId} onChanged={load} />
          ) : (
          <>
          {/* Status filter */}
          <div className="mt-3 flex flex-wrap gap-2" data-testid="status-filters">
            {STATUS_FILTERS.map((f) => {
              const n = f.key === "all" ? chCounts.total : chCounts[f.key];
              if (f.key === "pilot" && chCounts.pilot === 0) return null;
              return (
                <button key={f.key} data-testid={`status-filter-${f.key}`} onClick={() => setStatusFilter(f.key)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-all ${statusFilter === f.key
                    ? "border-slate-400/50 bg-slate-500/20 text-slate-100"
                    : "border-border bg-[#0d1016] text-slate-400 hover:text-slate-200"}`}>
                  {f.label} <span className="ml-1 font-mono text-[10px] text-slate-500">{n}</span>
                </button>
              );
            })}
          </div>

          {/* Passage grid */}
          {activeCh.passage_count === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-border py-16 text-center text-sm text-slate-400">This chapter has no passages planned yet.</div>
          ) : visible.length === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-border py-12 text-center text-sm text-slate-400">No passages match this filter.</div>
          ) : (
            <div className="mt-4 grid gap-2 lg:grid-cols-2" data-testid="passage-grid">
              {visible.map((p) => <PassageCard key={p.id} p={p} onOpen={setOpenId} />)}
            </div>
          )}
          </>
          )}
        </div>
      )}

      {openId && <PassageDetail pid={openId} onClose={() => setOpenId(null)} onChange={load} />}
    </div>
  );
}
